/**
 * Scope 3 Emissions Detail Page
 *
 * Value chain emissions (upstream and downstream).
 * Placeholder for future implementation.
 */

import { useParams, Link } from 'react-router-dom';
import { ArrowUp, ArrowDown, List, Bell } from 'lucide-react';
import { SCOPE_LUCIDE_ICONS, SCOPE_COLORS } from '../config/scopeBranding';
import { Dashboard } from '@g17eco/ui-modern/components/ui/dashboard';
import { Card, CardContent } from '@g17eco/ui-modern/components/ui/card';
import { Button } from '@g17eco/ui-modern/components/ui/button';
import { emissionsTrackerRoutes, rootAppPath } from '../routes';
import { generateUrl } from '@routes/util';
import { EmissionsScopeNav } from '../components/EmissionsNav';

export const EmissionsScope3Page = () => {
  const { initiativeId } = useParams<{ initiativeId: string }>();

  const activitiesUrl = generateUrl(emissionsTrackerRoutes.EMISSIONS_ACTIVITIES, {
    rootAppPath,
    initiativeId,
  });

  return (
    <Dashboard>
      {/* Scope Sub-Navigation */}
      <EmissionsScopeNav />

      {/* Header */}
      <div className='tw:mb-6'>
        <div className='tw:flex tw:justify-between tw:items-center'>
          <div>
            <h4 className='tw:text-xl tw:font-semibold tw:mb-1 tw:flex tw:items-center tw:gap-2'>
              <SCOPE_LUCIDE_ICONS.scope3 className='tw:size-5' style={{ color: SCOPE_COLORS.scope3 }} />
              Scope 3 Emissions
            </h4>
            <p className='tw:text-sm tw:text-muted-foreground'>
              Value chain emissions from upstream and downstream activities
            </p>
          </div>
        </div>
      </div>

      {/* Coming Soon Card */}
      <Card>
        <CardContent className='tw:p-10 tw:text-center'>
          <div
            className='tw:rounded-full tw:inline-flex tw:items-center tw:justify-center tw:mb-6'
            style={{
              width: 80,
              height: 80,
              backgroundColor: 'rgba(245, 158, 11, 0.1)',
            }}
          >
            <SCOPE_LUCIDE_ICONS.scope3 className='tw:size-8' style={{ color: SCOPE_COLORS.scope3 }} />
          </div>
          <h5 className='tw:text-lg tw:font-semibold tw:mb-3'>Scope 3 Coming Soon</h5>
          <p className='tw:text-sm tw:text-muted-foreground tw:mb-6 tw:max-w-lg tw:mx-auto'>
            Scope 3 emissions tracking covers all 15 categories of value chain emissions including
            purchased goods, transportation, employee commuting, and more. This feature is under
            development and will be available in a future release.
          </p>

          <div className='tw:flex tw:gap-3 tw:justify-center tw:mb-6'>
            <div className='tw:border tw:rounded-lg tw:p-3 tw:min-w-[140px] tw:text-center'>
              <div className='tw:font-semibold' style={{ color: SCOPE_COLORS.scope3 }}>Upstream</div>
              <div className='tw:text-xs tw:text-muted-foreground'>8 categories</div>
            </div>
            <div className='tw:border tw:rounded-lg tw:p-3 tw:min-w-[140px] tw:text-center'>
              <div className='tw:font-semibold' style={{ color: SCOPE_COLORS.scope3 }}>Downstream</div>
              <div className='tw:text-xs tw:text-muted-foreground'>7 categories</div>
            </div>
          </div>

          <div className='tw:flex tw:justify-center tw:gap-2'>
            <Button variant='outline' asChild>
              <Link to={activitiesUrl}>
                <List className='tw:size-4' />
                View Activities
              </Link>
            </Button>
            <Button disabled style={{ backgroundColor: SCOPE_COLORS.scope3, color: 'white' }}>
              <Bell className='tw:size-4' />
              Notify Me
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Scope 3 Categories Preview */}
      <div className='tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:gap-6 tw:mt-6'>
        <Card className='tw:h-full'>
          <CardContent className='tw:p-4'>
            <h5 className='tw:font-semibold tw:mb-3 tw:flex tw:items-center tw:gap-2'>
              <ArrowUp className='tw:size-4 tw:text-muted-foreground' />
              Upstream Categories
            </h5>
            <div className='tw:text-sm tw:text-muted-foreground tw:space-y-1.5'>
              <div>1. Purchased goods and services</div>
              <div>2. Capital goods</div>
              <div>3. Fuel and energy-related activities</div>
              <div>4. Upstream transportation and distribution</div>
              <div>5. Waste generated in operations</div>
              <div>6. Business travel</div>
              <div>7. Employee commuting</div>
              <div>8. Upstream leased assets</div>
            </div>
          </CardContent>
        </Card>
        <Card className='tw:h-full'>
          <CardContent className='tw:p-4'>
            <h5 className='tw:font-semibold tw:mb-3 tw:flex tw:items-center tw:gap-2'>
              <ArrowDown className='tw:size-4 tw:text-muted-foreground' />
              Downstream Categories
            </h5>
            <div className='tw:text-sm tw:text-muted-foreground tw:space-y-1.5'>
              <div>9. Downstream transportation and distribution</div>
              <div>10. Processing of sold products</div>
              <div>11. Use of sold products</div>
              <div>12. End-of-life treatment of sold products</div>
              <div>13. Downstream leased assets</div>
              <div>14. Franchises</div>
              <div>15. Investments</div>
            </div>
          </CardContent>
        </Card>
      </div>
    </Dashboard>
  );
};
